import React, { useState } from 'react';
import './Contact.css';
import { 
    FaEnvelope, 
    FaPhoneAlt, 
    FaMapMarkerAlt, 
    FaPaperPlane, 
    FaCheckCircle, 
    FaSpinner, 
    FaClock, 
    FaCopy, 
    FaCheck,
    FaComments,
    FaExternalLinkAlt,
    FaRedo
} from 'react-icons/fa';
import { SiGmail } from 'react-icons/si';
import { personalInfo } from '../data/personalInfo';
import { trackEvent } from '../utils/analytics';

// Configurable keys for direct in-browser delivery (Web3Forms or custom endpoint)
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || '';
const CONTACT_API_ENDPOINT = process.env.REACT_APP_CONTACT_API_URL || '';

const inquiryTypes = [
    { id: 'fulltime', label: 'Senior Full-Time Role', tpl: 'Discussing a Senior Engineering / Architecture role...' },
    { id: 'advisory', label: 'Architecture Advisory', tpl: 'Seeking architectural advisory on distributed systems...' },
    { id: 'consulting', label: 'Contract / Consulting', tpl: 'Inquiring regarding high-throughput backend / cloud implementation...' },
    { id: 'tech', label: 'Technical Discussion', tpl: 'Connecting regarding systems engineering & architecture...' },
];

const Contact = () => {
    const [selectedType, setSelectedType] = useState('fulltime');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
        honeypot: '', // anti-spam bot trap
    });
    const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'api-success' | 'client-ready' | 'error'
    const [feedback, setFeedback] = useState('');
    const [copiedField, setCopiedField] = useState(null);
    const [dispatchLinks, setDispatchLinks] = useState(null);
    const [copiedTransmission, setCopiedTransmission] = useState(false);

    const [submittedInfo, setSubmittedInfo] = useState(null);

    const handleCopy = (text, field) => {
        trackEvent('contact_channel_copy', { field });
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const handleCopyTransmission = () => {
        if (!dispatchLinks?.rawPayload) return;
        trackEvent('contact_hub_copy');
        navigator.clipboard.writeText(dispatchLinks.rawPayload);
        setCopiedTransmission(true);
        setTimeout(() => setCopiedTransmission(false), 2500);
    };

    const handleTypeSelect = (type) => {
        setSelectedType(type.id);
        if (!formData.message) {
            setFormData((prev) => ({ ...prev, message: type.tpl }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setStatus('idle');
        setFeedback('');
        setDispatchLinks(null);
        setSubmittedInfo(null);
        setFormData({ name: '', email: '', message: '', honeypot: '' });
    };

    /**
     * Multi-Rail Transmission Engine:
     * Rail 1: Direct In-Browser Delivery via Web3Forms (zero backend, zero CORS)
     * Rail 2: Optional Custom Serverless Endpoint (if REACT_APP_CONTACT_API_URL set)
     * Rail 3: Resilient Smart Dispatch (Direct Gmail Web compose + Native Mail Client + 1-Click Clipboard)
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.message) return;

        // Anti-bot honeypot check
        if (formData.honeypot) {
            setStatus('api-success');
            setFeedback('Transmission verified and recorded.');
            return;
        }

        setStatus('sending');
        setFeedback('');

        const activeScope = inquiryTypes.find((t) => t.id === selectedType)?.label || 'Engineering Inquiry';
        const subject = `[${activeScope}] From ${formData.name}`;
        const rawPayload = `Sender: ${formData.name}\nEmail: ${formData.email}\nScope: ${activeScope}\n\nMessage:\n${formData.message}\n\n---\nTransmitted via devamit.in terminal`;

        const mailtoUrl = `mailto:${personalInfo.contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(rawPayload)}`;
        const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(personalInfo.contact.email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(rawPayload)}`;

        const links = { gmailUrl, mailtoUrl, rawPayload, scope: activeScope };

        // Attempt Rail 1: Web3Forms Direct Delivery
        if (WEB3FORMS_ACCESS_KEY) {
            try {
                const response = await fetch('https://api.web3forms.com/submit', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify({
                        access_key: WEB3FORMS_ACCESS_KEY,
                        name: formData.name,
                        email: formData.email,
                        inquiry_scope: activeScope,
                        message: formData.message,
                        subject: subject,
                        from_name: `${formData.name} via devamit.in`,
                        botcheck: formData.honeypot,
                    }),
                });

                const data = await response.json();

                if (response.ok && data.success) {
                    trackEvent('contact_dispatched', { scope: activeScope, provider: 'web3forms' });
                    setSubmittedInfo({
                        name: formData.name,
                        email: formData.email,
                        scope: activeScope,
                    });
                    setFormData({ name: '', email: '', message: '', honeypot: '' });
                    setStatus('api-success');
                    setFeedback('Transmission secured! Your inquiry was delivered directly to Amit Kumar. Guaranteed response within 24 hours.');
                    setDispatchLinks(links);
                    return;
                }
                throw new Error(data.message || `Web3Forms responded with status ${response.status}`);
            } catch (apiErr) {
                console.warn('Web3Forms delivery error, activating Smart Dispatch fallback:', apiErr.message);
            }
        }

        // Attempt Rail 2: Custom Serverless Endpoint (if configured)
        if (CONTACT_API_ENDPOINT) {
            try {
                const response = await fetch(CONTACT_API_ENDPOINT, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: formData.name,
                        email: formData.email,
                        message: formData.message,
                        scope: activeScope,
                        honeypot: formData.honeypot,
                    }),
                });

                if (response.ok) {
                    trackEvent('contact_dispatched', { scope: activeScope, provider: 'aws_ses' });
                    setSubmittedInfo({
                        name: formData.name,
                        email: formData.email,
                        scope: activeScope,
                    });
                    setFormData({ name: '', email: '', message: '', honeypot: '' });
                    setStatus('api-success');
                    setFeedback(`Transmission secured! Your inquiry was delivered directly to Amit Kumar via AWS SES. Guaranteed response within 24 hours.`);
                    setDispatchLinks(links);
                    return;
                }
                throw new Error(`Server responded with ${response.status}`);
            } catch (apiErr) {
                console.warn('Serverless endpoint unavailable, activating Smart Dispatch fallback:', apiErr.message);
            }
        }

        // Rail 2: Fallback / Smart Multi-Action Dispatch
        await new Promise((resolve) => setTimeout(resolve, process.env.NODE_ENV === 'test' ? 0 : 600));
        trackEvent('contact_fallback_activated', { scope: activeScope });
        setDispatchLinks(links);
        setStatus('client-ready');
        setFeedback('Transmission formatted! Launch directly in Gmail Web, your default mail app, or copy with one click:');

        // Automatically trigger mailto as initial intent in real browsers
        if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
            try {
                window.location.href = mailtoUrl;
            } catch (_) {
                // Silently ignored if browser blocks automatic mailto
            }
        }
    };

    return (
        <section className="contact-section" id="contact">
            <div className="cyber-grid"></div>
            <div className="section-container">
                <div className="section-header">
                    <span className="section-badge">CONTACT &amp; INQUIRIES</span>
                    <h2 className="section-title">Initiate Engineering Dialogue</h2>
                    <p className="section-subtitle">
                        Available for senior/principal engineering roles, architectural advisory, and high-throughput systems design.
                    </p>
                </div>

                <div className="contact-grid">
                    {/* Form Column */}
                    <div className="contact-card form-card">
                        <div className="contact-card-header">
                            <div className="contact-icon-badge cyan">
                                <FaComments className="contact-icon cyan" />
                            </div>
                            <div className="contact-header-meta">
                                <span className="contact-card-tag cyan">Direct Transmission</span>
                                <h3 className="contact-box-title">Send a Message</h3>
                            </div>
                        </div>

                        {status === 'api-success' ? (
                            <div className="dispatch-success-state">
                                <div className="success-halo-icon">
                                    <FaCheckCircle className="success-main-icon" />
                                </div>
                                <span className="success-badge-cyber">TRANSMISSION DELIVERED DIRECTLY</span>
                                <h4 className="success-hero-title">Inquiry Dispatched Successfully</h4>
                                <p className="success-hero-desc">
                                    {feedback || "Transmission secured! Your inquiry was delivered directly to Amit Kumar. Guaranteed response within 24 hours."}
                                </p>

                                {submittedInfo && (
                                    <div className="success-receipt-box">
                                        <div className="receipt-row">
                                            <span className="receipt-label">Sender:</span>
                                            <span className="receipt-val">{submittedInfo.name}</span>
                                        </div>
                                        <div className="receipt-row">
                                            <span className="receipt-label">Return Email:</span>
                                            <span className="receipt-val">{submittedInfo.email}</span>
                                        </div>
                                        <div className="receipt-row">
                                            <span className="receipt-label">Scope:</span>
                                            <span className="receipt-val highlight">{submittedInfo.scope}</span>
                                        </div>
                                        <div className="receipt-row">
                                            <span className="receipt-label">Priority SLA:</span>
                                            <span className="receipt-val green">&lt; 24h Guaranteed</span>
                                        </div>
                                    </div>
                                )}

                                <button type="button" className="reset-inquiry-btn primary" onClick={resetForm}>
                                    <FaRedo className="btn-icon" /> Send Another Message
                                </button>
                            </div>
                        ) : (
                            <>
                                <p className="contact-box-desc">
                                    Select an engagement scope below or craft a custom transmission.
                                </p>

                                <div className="inquiry-type-selector">
                                    <span className="inquiry-label">Inquiry Scope:</span>
                                    <div className="inquiry-chips">
                                        {inquiryTypes.map((type) => (
                                            <button
                                                type="button"
                                                key={type.id}
                                                className={`inquiry-chip ${selectedType === type.id ? 'active' : ''}`}
                                                onClick={() => handleTypeSelect(type)}
                                            >
                                                {type.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <form className="contact-form" onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="name">Your Name / Organization</label>
                                        <input
                                            id="name"
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="e.g. Sarah Connor, TechCorp"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="email">Return Email</label>
                                        <input
                                            id="email"
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="e.g. sarah@techcorp.com"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="message">Project Scope / Message</label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            placeholder="Outline your vision, technical requirements, or role specifications..."
                                            rows="3"
                                            required
                                        ></textarea>
                                    </div>
                                    {/* Anti-bot honeypot field (hidden from real users) */}
                                    <input
                                        type="text"
                                        name="honeypot"
                                        value={formData.honeypot}
                                        onChange={handleChange}
                                        style={{ display: 'none' }}
                                        tabIndex="-1"
                                        autoComplete="off"
                                        aria-hidden="true"
                                    />

                                    <button
                                        type="submit"
                                        className="contact-submit-btn"
                                        disabled={status === 'sending'}
                                    >
                                        {status === 'sending' ? (
                                            <>
                                                <FaSpinner className="btn-spinner" /> Transmitting...
                                            </>
                                        ) : (
                                            <>
                                                <FaPaperPlane className="btn-icon" /> Send Message
                                            </>
                                        )}
                                    </button>
                                </form>

                                {status === 'client-ready' && dispatchLinks && (
                                    <div className="smart-dispatch-hub">
                                        <div className="dispatch-hub-header">
                                            <FaCheckCircle className="status-icon-cyan" />
                                            <span>{feedback}</span>
                                        </div>
                                        <div className="dispatch-actions-grid">
                                            <a
                                                href={dispatchLinks.gmailUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="dispatch-action-btn gmail"
                                            >
                                                <SiGmail className="action-icon" />
                                                <span>Open in Gmail Web</span>
                                                <FaExternalLinkAlt className="external-icon" />
                                            </a>
                                            <a
                                                href={dispatchLinks.mailtoUrl}
                                                className="dispatch-action-btn mailto"
                                            >
                                                <FaEnvelope className="action-icon" />
                                                <span>Default Mail App</span>
                                            </a>
                                            <button
                                                type="button"
                                                className={`dispatch-action-btn copy ${copiedTransmission ? 'copied' : ''}`}
                                                onClick={handleCopyTransmission}
                                            >
                                                {copiedTransmission ? (
                                                    <>
                                                        <FaCheck className="action-icon" />
                                                        <span>Copied to Clipboard!</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaCopy className="action-icon" />
                                                        <span>Copy Message Text</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {status === 'error' && feedback && (
                                    <div className="contact-status-msg error">
                                        <span>{feedback}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Direct Channels Column */}
                    <div className="contact-card channels-card">
                        <div className="contact-card-header">
                            <div className="contact-icon-badge purple">
                                <FaEnvelope className="contact-icon purple" />
                            </div>
                            <div className="contact-header-meta">
                                <span className="contact-card-tag purple">Direct Dispatch</span>
                                <h3 className="contact-box-title">Verified Channels</h3>
                            </div>
                        </div>

                        <p className="contact-box-desc">
                            Prefer asynchronous correspondence or direct network outreach? Connect via any verified endpoint below.
                        </p>

                        <div className="channels-list">
                            <div className="channel-item interactive">
                                <a href={`mailto:${personalInfo.contact.email}`} className="channel-main-link">
                                    <div className="channel-icon-wrap">
                                        <FaEnvelope className="channel-icon" />
                                    </div>
                                    <div className="channel-text-wrap">
                                        <div className="channel-label">Email Dispatch</div>
                                        <div className="channel-value">{personalInfo.contact.email}</div>
                                    </div>
                                </a>
                                <button
                                    type="button"
                                    className="channel-copy-btn"
                                    onClick={() => handleCopy(personalInfo.contact.email, 'email')}
                                    aria-label="Copy email address"
                                    title="Copy email"
                                >
                                    {copiedField === 'email' ? <FaCheck className="copied-icon" /> : <FaCopy />}
                                    <span className="copy-btn-text">{copiedField === 'email' ? 'Copied' : 'Copy'}</span>
                                </button>
                            </div>

                            <div className="channel-item interactive">
                                <a href={`tel:${personalInfo.contact.phone.replace(/\s+/g, '')}`} className="channel-main-link">
                                    <div className="channel-icon-wrap">
                                        <FaPhoneAlt className="channel-icon" />
                                    </div>
                                    <div className="channel-text-wrap">
                                        <div className="channel-label">Voice / Mobile</div>
                                        <div className="channel-value">{personalInfo.contact.phone}</div>
                                    </div>
                                </a>
                                <button
                                    type="button"
                                    className="channel-copy-btn"
                                    onClick={() => handleCopy(personalInfo.contact.phone, 'phone')}
                                    aria-label="Copy phone number"
                                    title="Copy phone"
                                >
                                    {copiedField === 'phone' ? <FaCheck className="copied-icon" /> : <FaCopy />}
                                    <span className="copy-btn-text">{copiedField === 'phone' ? 'Copied' : 'Copy'}</span>
                                </button>
                            </div>

                            <div className="channel-item static">
                                <div className="channel-icon-wrap">
                                    <FaMapMarkerAlt className="channel-icon" />
                                </div>
                                <div className="channel-meta-wrap">
                                    <div className="channel-label">Base Location &amp; Mobility</div>
                                    <div className="channel-value">{personalInfo.contact.location} (IST, UTC +5:30)</div>
                                    <div className="channel-sub-pill">● Remote &amp; Relocation Ready</div>
                                </div>
                            </div>

                            <div className="channel-item static">
                                <div className="channel-icon-wrap">
                                    <FaClock className="channel-icon" />
                                </div>
                                <div className="channel-text-wrap">
                                    <div className="channel-label">Turnaround Response SLA</div>
                                    <div className="channel-value">&lt; 24 Hours Guaranteed Response</div>
                                </div>
                            </div>
                        </div>

                        <div className="system-health-badge">
                            <span className="live-radar"></span>
                            <span>DISPATCH ACTIVE • RESPONSE SLA &lt; 24H • GLOBAL READINESS</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;